import { Injectable } from '@angular/core';
import { FilterService } from '../../../interfaces/filter/filter-service';
import { tap } from 'rxjs/operators';
import { MaterialCatalogueItemsQuery } from './material-catalogue-items.query';
import { MaterialCatalogueItemsStore } from './material-catalogue-items.store';
import { TableColumn } from '../../../models/table-column';
import { MaterialCatalogueBackendService } from '../../../services/material-catalogue/material-catalogue-backend.service';
import { IItemsServiceInterface } from '../../../interfaces/items/items-service.interface';
import { Observable } from 'rxjs';
import { MaterialCatalogueListItemQuery } from '../../../models/material-catalogue/material-catalogue-list-item-query.model';
import { LocalStoreConfigModel } from '../../../models/local-store-config.model';
import { MaterialCatalogueListItem } from 'src/app/core/models/client-models';

@Injectable({
  providedIn: 'root',
})
export class MaterialCatalogueItemsService
  implements FilterService, IItemsServiceInterface<MaterialCatalogueListItem>
{
  constructor(
    private materialCatalogueItemsStore: MaterialCatalogueItemsStore,
    private materialCatalogueItemsQuery: MaterialCatalogueItemsQuery,
    private materialCatalogueBackendService: MaterialCatalogueBackendService
  ) {}

  initializeStore(storeConfig?: LocalStoreConfigModel): void {
    this.resetStore(null);
    this.getNextPage();
  }

  selectActive(): Observable<MaterialCatalogueListItem> {
    return this.materialCatalogueItemsQuery.selectActive();
  }

  resetStore(storeConfig?: LocalStoreConfigModel): void {
    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      page: 0,
      currentPage: 0,
      pageSize: 50,
      lastPage: false,
    }));
    this.materialCatalogueItemsStore.remove();
  }

  setActive(
    item: MaterialCatalogueListItem,
    storeConfig?: LocalStoreConfigModel
  ): void {
    this.materialCatalogueItemsStore.setActive(item.materialCatalogueID);
  }

  changeSearchFilterValue(
    filter: string,
    storeConfig?: LocalStoreConfigModel
  ): void {
    this.resetStore(null);
    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      filterValue: filter,
    }));
    this.getNextPage();
  }

  clearSearchFilterValue(storeConfig?: LocalStoreConfigModel): void {
    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      filterValue: '',
    }));
  }

  setFilterOptions(
    filterSelection: any,
    storeConfig?: LocalStoreConfigModel
  ): void {
    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      filterOptions: filterSelection,
    }));
  }

  /**
   * Select next cost center list page.
   */
  getNextPage() {
    this.materialCatalogueItemsStore.setLoading(true);
    // Get values for http params.
    const nextPage: number =
      this.materialCatalogueItemsQuery.getValue().page + 1;
    this.setPage(nextPage);
    const pageSize: number =
      this.materialCatalogueItemsQuery.getValue().pageSize;

    let materialCatalogueParams = {
      pageNumber: nextPage,
      pageSize,
      filterFullText: true,
      full: false,
    } as MaterialCatalogueListItemQuery;

    materialCatalogueParams = this.setFilter(materialCatalogueParams);
    materialCatalogueParams = this.setSorting(materialCatalogueParams);
    this.materialCatalogueBackendService
      .getMaterialCatalogue(materialCatalogueParams)
      .pipe(tap(() => this.materialCatalogueItemsStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.materialCatalogueItemsStore.upsertMany(data.resultObject.items);
          this.materialCatalogueItemsStore.setError(null);
          if (data.resultObject.items.length === 0) {
            // if no results set page to previous.
            this.setPage(nextPage - 1);
            // set last page indicator.
            this.materialCatalogueItemsStore.update(
              (state) => (state.lastPage = true)
            );
          }
        },
        error: (error) =>
          this.materialCatalogueItemsStore.setError(error.error),
      });
  }

  setSorting(params: MaterialCatalogueListItemQuery) {
    const sortKey = this.materialCatalogueItemsQuery.getValue()?.sortKey;
    const sortDirection =
      this.materialCatalogueItemsQuery.getValue()?.sortDirection;

    params.sortBy = sortKey;
    params.sortOrder = sortDirection;

    return params;
  }

  setFilter(params: MaterialCatalogueListItemQuery) {
    const values = this.materialCatalogueItemsQuery.getValue()?.filterOptions;
    const filterValue =
      this.materialCatalogueItemsQuery.getValue()?.filterValue;

    const filterAll =
      !values.number.selected &&
      !values.description.selected &&
      !values.description2.selected &&
      !values.quantity.selected &&
      !values.unit.selected &&
      !values.price.selected;

    // eslint-disable-next-line id-blacklist
    params.number = values.number.selected || filterAll ? filterValue : '';
    params.name = values.description.selected || filterAll ? filterValue : '';
    params.description =
      values.description2.selected || filterAll ? filterValue : '';
    params.quantity = values.quantity.selected || filterAll ? filterValue : '';
    params.price = values.price.selected || filterAll ? filterValue : '';

    return params;
  }

  setPage(newPage: number) {
    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      page: newPage,
    }));
  }

  clearCache() {
    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      page: 0,
      currentPage: 0,
      pageSize: 50,
      lastPage: false,
    }));
    this.materialCatalogueItemsStore.remove();
  }

  showSelectedColumns(columns: TableColumn[]): void {
    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      columns,
    }));
  }

  filterValueChange(filterValue: string) {
    this.clearCache();
    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      filterValue,
    }));
    this.getNextPage();
  }

  selected(expense: MaterialCatalogueListItem) {
    this.materialCatalogueItemsStore.setActive(expense?.materialCatalogueID);
  }

  clearFilter() {
    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      filterValue: '',
    }));
  }

  onSort(
    sortKey: string,
    sortDirection: string,
    storeConfig?: LocalStoreConfigModel
  ): void {
    this.resetStore(null);

    if (sortDirection === '') {
      sortKey = '';
    }

    if (sortKey.indexOf('.') !== -1) {
      sortKey = sortKey.split('.')[1];
    }

    this.materialCatalogueItemsStore.update((state) => ({
      ...state,
      sortDirection,
      sortKey,
    }));

    this.getNextPage();
  }
}
